'use client';

import { Flex } from 'antd';
import React from 'react';

import StatusCard, { StatusCardVariant } from '../status-card/status-card';
import styles from './status-card-group.styles';

/**
 * Define la estructura de datos para una sola tarjeta dentro del StatusCardGroup.
 */
export type StatusCardData = {
  /**
   * Un identificador único para la tarjeta.
   */
  id: string;
  /**
   * El valor numérico que se mostrará en la tarjeta.
   */
  count: number;
  /**
   * La etiqueta o título que describe el estado de la tarjeta.
   */
  label: string;
  /**
   * La variante visual de la tarjeta.
   */
  variant: StatusCardVariant;
  /**
   * Si es verdadero, la tarjeta estará deshabilitada visual y funcionalmente.
   */
  disabled?: boolean;
};

/**
 * Props para el componente StatusCardGroup.
 */
export type StatusCardGroupProps = {
  /**
   * Un array de objetos `StatusCardData` que se representarán como StatusCards.
   */
  cards: StatusCardData[];
  /**
   * Un array de ID de tarjetas que deben representarse en un estado 'activo'.
   */
  activeCards?: string[];
  /**
   * Una función de devolución de llamada que se ejecuta cuando se hace clic en una tarjeta.
   *
   * @param {string} cardId - El ID de la tarjeta en la que se hizo clic.
   *
   * @param {boolean} isActive - El nuevo estado activo potencial de la tarjeta.
   */
  onCardToggle?: (cardId: string, isActive: boolean) => void;
  /**
   * Si es verdadero, el componente mostrará un estado de carga (p. Ej., mostrando los recuentos como 0)..
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Un nombre de clase CSS opcional para aplicar al contenedor del grupo.
   */
  className?: string;
  /**
   * Estilos personalizados opcionales que se aplicarán al contenedor del grupo.
   */
  style?: React.CSSProperties;
  mobileTwoThreeLayout?: boolean;
};

/**
 * Un componente que muestra un grupo de StatusCards y gestiona sus estados activos.
 *
 * @param {StatusCardGroupProps} props - Las props para el componente StatusCardGroup.
 *
 * @returns {JSX.Element} El componente StatusCardGroup renderizado.
 */
const StatusCardGroup = ({
  cards,
  activeCards = [],
  onCardToggle,
  loading = false,
  className,
  style,
  mobileTwoThreeLayout
}: StatusCardGroupProps) => {
  const delegatedStyles = {
    ...style
  };

  const handleCardClick = (cardId: string) => {
    if (onCardToggle) {
      const isCurrentlyActive = activeCards.includes(cardId);
      onCardToggle(cardId, !isCurrentlyActive);
    }
  };

  return (
    <Flex
      className={className}
      style={{
        ...styles.statusCardGroup,
        ...delegatedStyles
      }}
      wrap='wrap'>
      {cards.map((cardData, index) => {
        const baseWidth = mobileTwoThreeLayout
          ? index < 2
            ? 'calc((100% - 12px) / 2)'
            : 'calc((100% - 24px) / 3)'
          : undefined;
        const itemStyle: React.CSSProperties | undefined = mobileTwoThreeLayout
          ? {
              width: baseWidth,
              flexDirection: 'column' as React.CSSProperties['flexDirection'],
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center' as React.CSSProperties['textAlign'],
              gap: 8
            }
          : undefined;
        return (
          <StatusCard
            key={cardData.id}
            count={loading ? 0 : cardData.count}
            label={cardData.label}
            variant={cardData.variant}
            active={activeCards.includes(cardData.id)}
            disabled={cardData.disabled || (cardData.count === 0 && !loading)}
            onClick={() => handleCardClick(cardData.id)}
            style={itemStyle}
          />
        );
      })}
    </Flex>
  );
};

export default StatusCardGroup;
